import React, { useState } from 'react';
import { format, parse, isValid } from 'date-fns';
import { Calendar as CalendarIcon } from 'lucide-react';
import { DayPicker } from 'react-day-picker';
import 'react-day-picker/dist/style.css';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';

/**
 * DatePicker Component with Calendar Popup and MM/DD/YYYY format
 * 
 * Usage:
 * <DatePicker 
 *   value={dateString}  // YYYY-MM-DD format
 *   onChange={(value) => setDate(value)}  // Returns YYYY-MM-DD for backend
 *   className="..."
 *   disabled={false}
 * />
 */
const DatePicker = ({ value, onChange, className = "", disabled = false, required = false, min = null, max = null, disableWeekends = false, ...props }) => {
  const [open, setOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(() => {
    // Initialize with selected date's month or current date
    if (!value) return new Date();
    try {
      const date = parse(value, 'yyyy-MM-dd', new Date());
      return isValid(date) ? date : new Date();
    } catch {
      return new Date();
    }
  });

  // Convert YYYY-MM-DD to Date object
  const parseValue = (dateStr) => {
    if (!dateStr) return undefined;
    try {
      const date = parse(dateStr, 'yyyy-MM-dd', new Date());
      return isValid(date) ? date : undefined;
    } catch {
      return undefined;
    }
  };

  // Convert Date object to YYYY-MM-DD for backend
  const formatForBackend = (date) => {
    if (!date || !isValid(date)) return '';
    return format(date, 'yyyy-MM-dd');
  };

  // Convert YYYY-MM-DD to MM/DD/YYYY for display
  const formatForDisplay = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = parse(dateStr, 'yyyy-MM-dd', new Date());
      return isValid(date) ? format(date, 'MM/dd/yyyy') : '';
    } catch {
      return '';
    }
  };

  const selectedDate = parseValue(value);
  const displayValue = formatForDisplay(value);

  // Handler for popover open state change - reset month when opening
  const handleOpenChange = (isOpen) => {
    if (isOpen) {
      // When opening, reset to selected date's month or current month
      const parsedDate = parseValue(value);
      setCurrentMonth(parsedDate || new Date());
    }
    setOpen(isOpen);
  };

  const handleSelect = (date) => {
    if (date && onChange) {
      const backendValue = formatForBackend(date);
      // Create a synthetic event to match Input's onChange signature
      onChange({ target: { value: backendValue } });
    }
    setOpen(false);
  };

  const handleInputChange = (e) => {
    const inputValue = e.target.value;
    
    // Allow user to type in MM/DD/YYYY format
    if (inputValue) {
      try {
        const date = parse(inputValue, 'MM/dd/yyyy', new Date());
        if (isValid(date)) {
          // Check if weekend dates should be blocked
          if (disableWeekends) {
            const day = date.getDay();
            if (day === 0 || day === 6) {
              // Don't update if it's a weekend
              return;
            }
          }
          const backendValue = formatForBackend(date);
          if (onChange) {
            onChange({ target: { value: backendValue } });
          }
        }
      } catch {
        // Invalid format, just update the display
        if (onChange) {
          onChange(e);
        }
      }
    } else {
      // Empty value
      if (onChange) {
        onChange({ target: { value: '' } });
      }
    }
  };

  const minDate = min ? parseValue(min) : null;
  const maxDate = max ? parseValue(max) : null;

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <div className="relative">
          <input
            type="text"
            value={displayValue}
            onChange={handleInputChange}
            placeholder="MM/DD/YYYY"
            disabled={disabled}
            required={required}
            className={`flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 pr-10 ${className}`}
            onFocus={() => !disabled && setOpen(true)}
            {...props}
          />
          <Button
            type="button"
            variant="ghost"
            className="absolute right-0 top-0 h-10 w-10 p-0"
            onClick={() => !disabled && setOpen(!open)}
            disabled={disabled}
            tabIndex={-1}
          >
            <CalendarIcon className="h-4 w-4 text-gray-500" />
          </Button>
        </div>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <DayPicker
          mode="single"
          selected={selectedDate}
          onSelect={handleSelect}
          month={currentMonth}
          onMonthChange={setCurrentMonth}
          disabled={(date) => {
            if (disabled) return true;
            if (minDate && date < minDate) return true;
            if (maxDate && date > maxDate) return true;
            // Disable weekends if disableWeekends prop is true
            if (disableWeekends) {
              const day = date.getDay();
              if (day === 0 || day === 6) return true; // 0 = Sunday, 6 = Saturday
            }
            return false;
          }}
          fromDate={minDate || undefined}
          toDate={maxDate || undefined}
        />
      </PopoverContent>
    </Popover>
  );
};

export default DatePicker;
